/**
 * EarthVoice v2 — Main entry point
 * Wires together: Globe, Voice, AI Intent Parser, Query Router, UI, DuckDB
 */
import './ui/styles.css'
import Globe from './globe/globe.js'
import LayerManager from './globe/layers.js'
import Animator from './globe/animations.js'
import SpeechInput from './voice/speech-input.js'
import SpeechOutput from './voice/speech-output.js'
import { needsMicConsent, showMicConsentModal } from './voice/mic-consent.js'
import { parseIntent, streamEducationalResponse } from './ai/intent-parser.js'
import { classifyTurn } from './ai/conversation-router.js'
import { layerRole } from './ai/layer-roles.js'
import { contextStripChips, nextContextState } from './ui/context-strip.js'
import QueryRouter from './ai/query-router.js'
import { adaptIntent } from './ai/legacy-adapter.js'
import { executePipeline, createContext } from './ai/pipeline-executor.js'
import { initRegistry } from './ai/operation-registry.js'
import { initRasterCatalog } from './data/raster-catalog.js'
import UIOverlay from './ui/overlay.js'
import Legend from './ui/legend.js'
import { loadDataset, preloadEssentials } from './data/loader.js'
import { buildLocationIndex } from './utils/geo-utils.js'
import { setIsoToNameMap } from './data/natural-earth.js'
import { duckdbEngine, ensureDuckDB } from './data/duckdb-engine.js'
import { schedulePrewarm } from './utils/idle-prewarm.js'
import {
  prewarmGeolibre,
  waitGeolibreReady,
  isGeolibreReady,
  setGeolibreStatusHandler,
} from './data/geolibre-engine.js'
import { requiredEngines } from './ai/engine-requirements.js'
import { createLogger } from './utils/logger.js'
import { adminBoundaries } from './data/admin-boundaries.js'
import { analytics } from './utils/analytics.js'
import { initLicense, licenseHeaders } from './utils/license.js'
import { initClassroom, classroomHeaders, getClassroom } from './utils/classroom.js'
import { reportStepResult, flushResults, retryPendingResults } from './lessons/result-reporter.js'
import {
  masteryStorageKey,
  loadMastery,
  saveMastery,
  updateMastery,
} from './lessons/mastery-lite.js'
import globeContext from './state/globe-context.js'
import telemetry from './utils/telemetry.js'
import LessonPanel from './lessons/lesson-panel.js'
import LessonBrowser from './lessons/lesson-browser.js'
import { resolveClickedCountry } from './lessons/map-click.js'
import globeSwitcher from './globe/globe-switcher.js'
import { initStarfield } from './ui/starfield.js'
import MoonQueryRouter from './ai/moon-query-router.js'
import {
  addMarkers as moonAddMarkers,
  clearMarkers as moonClearMarkers,
  showPopup as moonShowPopup,
} from './globe/moon-globe.js'
import { buildLunarLocationIndex } from './data/lunar-data.js'

// ── App State ──────────────────────────────────────────────
// Ops that adjust the current view without replacing its data — the result
// table from the previous command still describes what is on the globe.
const RESULT_PRESERVING_OPS = new Set([
  'choropleth_style',
  'choropleth_scale',
  'layer_control',
  'adjust_opacity',
  'compass',
  'projection',
  'terrain',
  'graticule',
])

function isResultPreservingIntent(intent) {
  const ops = Array.isArray(intent?.pipeline) ? intent.pipeline.map((s) => s?.op) : [intent?.type]
  return ops.length > 0 && ops.every((op) => RESULT_PRESERVING_OPS.has(op))
}

const state = {
  isReady: false,
  isProcessing: false,
  locationIndex: null,
  countriesData: null,
  citiesData: null,
  duckdbStatus: 'idle', // idle | loading | ready | error
  geolibreStatus: 'idle', // idle | loading | ready | error
  moonRouter: null,
}

const log = createLogger('main')

/** Race a promise against a timeout so the cold-start gate can never hang. */
function withTimeout(promise, ms, label = 'warm-up') {
  return Promise.race([
    promise,
    new Promise((_resolve, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ])
}

// ── Splash Screen ──────────────────────────────────────────
const splash = document.getElementById('splash')
const splashStatus = document.getElementById('splash-status')

function setSplashStatus(text) {
  if (splashStatus) splashStatus.textContent = text
}

function hideSplash() {
  if (splash) splash.classList.add('hidden')
}

// ── Initialize ─────────────────────────────────────────────
async function init() {
  try {
    // CL-131: capture ?license=... before anything else so the very first
    // API request already carries the school's X-EV-License header.
    initLicense()

    // CL-145: capture ?class=... next — students arriving via a teacher's
    // join link pick an anonymous handle and adopt the school's license.
    initClassroom()

    // CL-146: retry lesson results queued while the network was down
    // (fail-soft: rejections are contractually impossible, but stay safe).
    retryPendingResults().catch(() => {})

    setSplashStatus('Building globe...')

    // 1. Initialize the globe
    const globeContainer = document.getElementById('globe-container')
    const globe = new Globe(globeContainer)

    window._globe = globe

    // 1b. Initialize star field canvas (hidden until transition)
    initStarfield()

    // 1c. Initialize globe switcher with the Earth globe
    globeSwitcher.init(globe)

    // 1d. DuckDB-WASM (~33 MB) is LAZY (CL-140, fixes BUG-050): nothing
    // downloads at page load. Init is triggered by whichever comes first:
    //   (a) on-demand — the cold-start gate in processCommand awaits
    //       duckdbEngine.waitReady() when an intent needs the 'duckdb'
    //       engine, and every consumer (query/waitReady/ensureDuckDB,
    //       boundary-feature-service single-feature retrieval) joins the
    //       same memoized init promise; or
    //   (b) background prewarm — first user interaction (pointer/key/touch)
    //       or browser idle (12 s fallback), so analytics users usually
    //       find the engine warm without initial page load paying 33 MB.
    // Status flows through onStatusChange; UI wiring is added at step 9.
    duckdbEngine.onStatusChange = (s) => {
      state.duckdbStatus = s
    }
    state.duckdbPrewarm = schedulePrewarm(
      () => {
        ensureDuckDB()
          .then(() => log.info('DuckDB-WASM ready (prewarm)'))
          .catch((err) => log.warn('DuckDB prewarm failed:', err.message))
      },
      { idleTimeoutMs: 12000 }
    )

    // 1d-bis. Pre-warm geolibre-wasm (~22MB, the geometry engine behind
    // buffer_zone) during browser idle — a few seconds after load so it never
    // competes with first paint, the basemap, or the DuckDB download. By the
    // time a student issues a buffer query it's usually ready; if not, the
    // cold-start gate in processCommand shows a "warming up" state and waits.
    setGeolibreStatusHandler((s) => {
      state.geolibreStatus = s
    })
    const scheduleIdle = (fn) =>
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback(fn, { timeout: 8000 })
        : setTimeout(fn, 5000)
    scheduleIdle(() => {
      prewarmGeolibre().then((ok) => log.info('geolibre prewarm', ok ? 'ready' : 'failed'))
    })

    // 1e. Initialize pipeline operation registry. Kicked off in parallel
    // with DuckDB but tracked on state so processCommand can await it
    // before validating any pipeline. Without this, the user can fire a
    // command after splash hides but before late-registered ops (e.g.
    // joshua_project) are available, and validatePipeline rejects the
    // step with "unknown operation".
    state.registryReady = initRegistry().catch((err) => {
      console.warn('[EarthVoice] Pipeline registry init failed:', err.message)
    })

    // 1f. Initialize raster catalog (non-blocking)
    initRasterCatalog().catch((err) => {
      console.warn('[EarthVoice] Raster catalog init failed:', err.message)
    })

    // 2. Initialize layer manager + legend
    const layerManager = new LayerManager(globe)
    const legend = new Legend()
    layerManager.onLegendUpdate((config) => legend.update(config))

    // Debug handles — only when ?debug=1. Lets live-session debugging drive
    // pipeline stages individually (engine queries, renders) from the console
    // instead of guessing from the outside. Added 2026-06-12 while hunting a
    // main-thread hang in the suitability render path.
    if (typeof location !== 'undefined' && /[?&]debug=1/.test(location.search)) {
      window.__ev = { layerManager, duckdbEngine, globe }
      console.warn('[EarthVoice] Debug handles exposed on window.__ev')
    }
    // 3. Initialize animator with empty index (updated after data loads)
    const animator = new Animator(globe, new Map())

    // 4. Initialize voice
    setSplashStatus('Initializing voice...')
    const speechInput = new SpeechInput()
    const speechOutput = new SpeechOutput()

    // 5. Initialize UI
    const uiOverlay = new UIOverlay(document.getElementById('ui-overlay'))
    // CL-262: while the result table is open, centre the globe in the space to
    // its right (it covered western Japan). Narrow screens stack the panel over
    // the map anyway, so no inset there.
    uiOverlay.onResultPanelChange((panel) => {
      globe.setLeftInset(() =>
        panel && panel.classList.contains('visible') && window.innerWidth > 900
          ? panel.offsetLeft + panel.offsetWidth
          : 0
      )
    })

    // CL-152 (BUG-045): Safari/Firefox/iPad have no Web Speech recognition —
    // switch the bottom bar to text-first mode instead of a dead mic. Every
    // voice entry point (mic button, spacebar) redirects to the text input.
    if (!speechInput.isSupported) {
      uiOverlay.setVoiceUnavailable()
      log.warn('speech recognition unavailable — text-first mode enabled (BUG-045)')
    }

    // Wire layer manager status callback
    layerManager.onStatusUpdate((msg) => {
      if (msg) {
        uiOverlay.setProcessing(msg)
      }
    })

    // Re-append legend to overlay (since UIOverlay rebuilds the container)
    document.getElementById('ui-overlay').appendChild(legend.panel)

    // Wire choropleth mode switcher UI (handler set after router init below)
    const csm = layerManager.choroplethStyleManager
    csm.onModeChange = (mode) => {
      if (mode === null) {
        uiOverlay.showModeSwitcher(false)
      } else {
        uiOverlay.showModeSwitcher(true)
        uiOverlay.setChoroplethMode(mode)
      }
    }
    // Wire tier legend updates from ChoroplethStyleManager → Legend panel
    csm.onLegendUpdate = (config) => {
      legend.update(config)
    }
    // Show mode switcher whenever ChoroplethStyleManager gets data
    const origSetData = csm.setData.bind(csm)
    csm.setData = (data, precomputedBreaks) => {
      origSetData(data, precomputedBreaks)
      uiOverlay.showModeSwitcher(true)
      uiOverlay.setChoroplethMode(csm.getCurrentMode())
    }
    const origSetPreclassified = csm.setPreclassifiedData.bind(csm)
    csm.setPreclassifiedData = (opts) => {
      origSetPreclassified(opts)
      uiOverlay.showModeSwitcher(true)
      uiOverlay.setChoroplethMode(csm.getCurrentMode())
    }
    // Hide mode switcher on layer clear
    const origClear = csm.clear.bind(csm)
    csm.clear = () => {
      origClear()
      uiOverlay.showModeSwitcher(false)
    }

    // 5b. Initialize Lesson Mode (schema-driven)
    const lessonPanel = new LessonPanel(document.getElementById('ui-overlay'), {
      onCommand: async (command) => {
        await processCommand(command, uiOverlay, router)
      },
      routeIntent: async (intent) => {
        await router.route(intent)
      },
      parseIntent: async (command) => {
        return await parseIntent(command)
      },
      speak: async (text) => {
        await speechOutput.speak(text)
      },
      // CL-139 (roadmap H1.2): fail-closed AI grading via the dedicated
      // POST /api/grade endpoint. The old path regex-scraped /api/intent and
      // returned { correct: true } on ANY failure — errors auto-graded as
      // correct. Now every failure resolves to the neutral 'review' verdict;
      // the lesson engine scores it neutrally and the Worker queues the
      // answer for the teacher (grading_reviews, migration 0008).
      gradeOpenResponse: async (question, answer, opts = {}) => {
        const apiUrl = import.meta.env.VITE_API_URL || ''
        // Worker enum: only send grade_band when it matches; omit otherwise.
        const gradeBand = /^(K-2|3-5|6-8|9-12)$/.test(opts.gradeBand || '') ? opts.gradeBand : null
        try {
          const resp = await fetch(`${apiUrl}/api/grade`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...licenseHeaders(),
              ...classroomHeaders(),
            },
            body: JSON.stringify({
              question,
              student_answer: String(answer).slice(0, 2000),
              ...(opts.expectedPoints?.length ? { expected_points: opts.expectedPoints } : {}),
              ...(gradeBand ? { grade_band: gradeBand } : {}),
            }),
          })
          if (!resp.ok) throw new Error(`grade API ${resp.status}`)
          // { verdict: 'correct'|'partial'|'incorrect'|'review', confidence?, feedback }
          return await resp.json()
        } catch {
          // Fetch/parse failure — fail closed client-side too.
          return {
            verdict: 'review',
            feedback: 'Your answer has been saved for your teacher to review.',
          }
        }
      },
      // map_click questions (roadmap H1.1): the engine registers a one-shot
      // click resolver here. Clicks are resolved to a country via
      // point-in-polygon against the loaded Natural Earth countries, so the
      // assessment works no matter which layers are rendered on the globe.
      // CL-146 (roadmap H2.2): step-level result persistence. The engine
      // buffers every scored step and flushes at lesson finish/stop; the
      // reporter no-ops for anonymous sessions and queues failed batches in
      // localStorage — persistence never blocks or breaks a lesson.
      reportStepResult,
      flushResults,
      registerMapClick: (handler) => {
        const map = globe.map
        const onClick = (e) => {
          handler(resolveClickedCountry(e.lngLat.lng, e.lngLat.lat, state.countriesData))
        }
        map.on('click', onClick)
        const canvas = map.getCanvas()
        const prevCursor = canvas.style.cursor
        canvas.style.cursor = 'crosshair'
        return () => {
          map.off('click', onClick)
          if (canvas.style.cursor === 'crosshair') canvas.style.cursor = prevCursor
        }
      },
    })

    // CL-163 (roadmap H2.4): release-stage observation logging. While the
    // lesson is in its freeform ("Release") step, every student query is an
    // invisible-assessment observation — tag telemetry with lesson, step,
    // and concepts. Cleared the moment the lesson leaves freeform, so
    // ordinary exploration is never tagged.
    // CL-173 (roadmap H3.2b): lessons with layer_mode:"stack" auto-enable
    // persistent stacking for their duration, restoring the student's prior
    // mode at the summary. G7/G8 overlay activities are load-bearing on this.
    let stackModeBeforeLesson = null
    lessonPanel.engine.on('state-changed', ({ to }) => {
      try {
        const engine = lessonPanel.engine
        if (engine.lesson?.layer_mode === 'stack') {
          if (to === 'SUMMARY') {
            if (stackModeBeforeLesson !== null) {
              layerManager.setStackMode(stackModeBeforeLesson)
              stackModeBeforeLesson = null
            }
          } else if (stackModeBeforeLesson === null) {
            stackModeBeforeLesson = layerManager.isStackMode()
            layerManager.setStackMode(true)
          }
        }
      } catch (err) {
        log.warn('lesson stack-mode glue failed (ignored)', err)
      }
      try {
        const engine = lessonPanel.engine
        if (to === 'FREEFORM' && engine.lesson) {
          const step = engine.currentStep
          telemetry.setLessonObservation({
            lessonId: engine.lesson.id,
            stepId: step?.id,
            concepts:
              step?.concepts ??
              engine.lesson.metadata?.standards ??
              engine.lesson.metadata?.tags ??
              [],
          })
        } else {
          telemetry.setLessonObservation(null)
        }
      } catch (err) {
        log.warn('observation tagging failed (ignored)', err)
      }
    })

    // CL-151 (roadmap H2.3): mastery-lite — fold every scored question into
    // the per-concept running score, keyed by classroom identity (CL-145,
    // zero PII). Records only; nothing routes on it in v1. Fail-soft: a
    // mastery/storage error must never break a lesson.
    lessonPanel.engine.on('question-answered', (result) => {
      try {
        const classroom = getClassroom()
        const key = masteryStorageKey(classroom?.classCode, classroom?.handle)
        const store = loadMastery(key)
        updateMastery(store, result.conceptTags || [], result.verdict)
        saveMastery(key, store)
      } catch (err) {
        log.warn('mastery update failed (ignored)', err)
      }
    })

    // Lesson browser: shows catalog when user clicks Lessons button
    const lessonBrowser = new LessonBrowser(document.getElementById('ui-overlay'), (lessonUrl) => {
      lessonPanel.startLesson(lessonUrl)
    })

    // Wire lesson panel "open-browser" event
    lessonPanel.onEvent('open-browser', () => {
      const body = lessonPanel.panel.querySelector('#lesson-body')
      const nav = lessonPanel.panel.querySelector('#lesson-nav')
      lessonBrowser.show(body, nav)
    })

    // 6. Initialize query router
    const router = new QueryRouter({
      animator,
      layerManager,
      speechOutput,
      locationIndex: new Map(),
      countriesData: null,
    })

    // Wire up result updates to UI
    router.onResultUpdate = (result) => {
      state.resultSeq = (state.resultSeq || 0) + 1
      uiOverlay.showResultPanel(result, router.getLastSQL(), router.getLastSpatialAdvice())
    }

    // Click a row in the results panel → fly the globe to that feature.
    // Only fires for rows that carry a _centroid (e.g. division/admin rows).
    uiOverlay.setResultRowClick((row) => {
      const c = row?._centroid
      if (Array.isArray(c) && c.length === 2) {
        animator.globe.flyTo(c[0], c[1], 12)
      }
    })

    // 6a. Pipeline context factory — creates fresh context per command
    // Services are stable references; data properties reset each execution.
    state.pipelineServices = {
      animator,
      layers: layerManager,
      speech: speechOutput,
      locationIndex: new Map(),
      countriesData: null,
      adminBoundaries: null,
      adminPMTiles: router.adminPMTiles,
      duckdb: duckdbEngine,
      onResultUpdate: router.onResultUpdate,
    }

    // Wire PMTiles mode switching + legend
    const pmtiles = router.adminPMTiles
    pmtiles.onModeChange = (mode) => {
      if (mode === null) {
        uiOverlay.showModeSwitcher(false)
      } else {
        uiOverlay.showModeSwitcher(true)
        uiOverlay.setChoroplethMode(mode)
      }
    }
    pmtiles.onLegendUpdate = (config) => {
      legend.update(config)
    }

    // Unified mode button handler: drives H3, PMTiles, or CSM depending on which is active
    uiOverlay.onChoroplethModeChange((mode) => {
      if (layerManager.hasH3Density()) {
        layerManager.setH3Mode(mode)
        uiOverlay.setChoroplethMode(mode)
      } else if (pmtiles.hasData()) {
        pmtiles.setMode(mode)
      } else if (csm.hasData()) {
        csm.setMode(mode)
      }
    })

    // Wire H3 density mode changes to the shared mode switcher UI
    layerManager._onH3ModeChange = (mode) => {
      if (mode === null) {
        uiOverlay.showModeSwitcher(false)
      } else {
        uiOverlay.showModeSwitcher(true)
        uiOverlay.setChoroplethMode(mode)
      }
    }

    // 6b. Initialize Moon query router (for lunar commands)
    state.moonRouter = new MoonQueryRouter({
      speechOutput,
      moonMarkers: {
        addMarkers: moonAddMarkers,
        clearMarkers: moonClearMarkers,
        addPopup: moonShowPopup,
      },
    })

    // Pre-build lunar location index
    buildLunarLocationIndex()

    // 7. Load data (includes pre-clipped boundaries for artifact-free fills)
    setSplashStatus('Loading world data...')
    const essentials = await preloadEssentials((msg) => setSplashStatus(msg))

    state.countriesData = essentials.countries
    state.citiesData = essentials.cities

    // Build location index
    setSplashStatus('Building location index...')
    state.locationIndex = buildLocationIndex(
      state.countriesData,
      null, // states loaded lazily
      state.citiesData
    )

    // Set up country name lookup
    if (state.countriesData) {
      setIsoToNameMap(state.countriesData)
    }

    // Update animator and router with loaded data
    animator.setLocationIndex(state.locationIndex)
    router.update({
      locationIndex: state.locationIndex,
      countriesData: state.countriesData,
    })
    // Keep pipeline services in sync
    state.pipelineServices.locationIndex = state.locationIndex
    state.pipelineServices.countriesData = state.countriesData

    // 8. Lazy-load states/provinces + 50m cities + 50m clipped countries in background
    loadDetailedDataInBackground(animator, router, layerManager)

    // 9. DuckDB-WASM is lazy (CL-140) — wire status changes to the UI
    // indicator now that uiOverlay exists, and push the current status in
    // case the prewarm already fired during splash.
    duckdbEngine.onStatusChange = (s) => {
      state.duckdbStatus = s
      uiOverlay.setDuckDBStatus(s)
    }
    if (state.duckdbStatus !== 'idle') uiOverlay.setDuckDBStatus(state.duckdbStatus)

    // 10. Initialize Admin Boundary Service in background (non-blocking)
    adminBoundaries.init().then(() => {
      router.update({ adminBoundaries })
      state.pipelineServices.adminBoundaries = adminBoundaries
      console.log('[EarthVoice] Admin boundary service ready')
    })

    // ── Wire Voice → AI → Router pipeline ──────────────────
    speechInput.onStart = () => {
      uiOverlay.setListening()
    }

    speechInput.onInterim = (text) => {
      uiOverlay.setInterimTranscript(text)
    }

    speechInput.onResult = async (transcript) => {
      state.lastInputMethod = 'voice'
      telemetry.startTimer('voice')
      await processCommand(transcript, uiOverlay, router)
    }

    // Wire globe context: update camera on moveend
    globe.map.on('moveend', () => {
      const center = globe.map.getCenter()
      globeContext.updateCamera(
        [center.lng, center.lat],
        globe.map.getZoom(),
        globe.map.getPitch(),
        globe.map.getBearing()
      )
    })

    speechInput.onEnd = () => {
      if (!state.isProcessing) {
        // Only reset to idle if we're not processing a command
      }
    }

    speechInput.onError = (message) => {
      uiOverlay.setError(message)
      setTimeout(() => uiOverlay.setIdle(), 4000)
    }

    // ── Wire UI controls ───────────────────────────────────
    uiOverlay.onVoiceToggle = () => {
      // CL-135: first-use mic disclosure — both the mic button and the
      // spacebar binding route through this handler, so gating here covers
      // every path that can start listening. Accepting starts listening
      // immediately so the user's original action completes.
      if (needsMicConsent()) {
        showMicConsentModal({ onAccept: () => speechInput.toggle() })
        return
      }
      speechInput.toggle()
    }

    uiOverlay.onTextSubmit = async (text) => {
      state.lastInputMethod = 'typed'
      await processCommand(text, uiOverlay, router)
    }

    // Shared reset handler — closes over init()'s local refs (layerManager,
    // legend, globe, speechOutput) so it can be invoked from both the UI
    // reset button and the processCommand 'reset' intent branch.
    state.handleReset = async () => {
      globeContext.resetChain()
      // CL-173 (H3.2b): reset means "start over" — exit stack mode and
      // force-clear past the overlay/stack gates.
      layerManager.setStackMode(false)
      uiOverlay.setLayersActive(false)
      layerManager.clearLayers({ force: true })
      clearContextStrip()
      legend.hide()
      globe.clearBoundaries()
      if (router.adminPMTiles) router.adminPMTiles.clear()
      if (globe.overture) globe.overture.removeAll()
      try {
        const { clearRouting } = await import('./layers/routing-layer.js')
        clearRouting(globe.map)
      } catch {}
      try {
        const { clearAllRasterLayers } = await import('./ai/ops/render-raster.js')
        clearAllRasterLayers(globe.map)
      } catch {}
      if (globe.map) globe.map.setTerrain({ source: 'maptiler-terrain', exaggeration: 0 })
      globe.resetView()
      uiOverlay.setIdle()
      uiOverlay.hideResultPanel()
      uiOverlay.showModeSwitcher(false)
      speechOutput.stop()
    }

    uiOverlay.onReset(state.handleReset)

    // CL-173 (roadmap H3.2b): stack mode + layer sidebar. The button toggles
    // persistent stacking; the sidebar is a live view of the CL-165 stack
    // (voice layer_control drives the same entries). Design:
    // docs/plans/H3.2B-LAYER-SIDEBAR.md
    const { LayerSidebar } = await import('./ui/layer-sidebar.js')
    // eslint-disable-next-line no-new
    new LayerSidebar(document.getElementById('ui-overlay') || document.body, layerManager)
    layerManager.onStackChanged(() => uiOverlay.setLayersActive(layerManager.isStackMode()))
    uiOverlay.onLayers(() => {
      layerManager.setStackMode(!layerManager.isStackMode())
    })

    uiOverlay.onLessons(() => {
      lessonPanel.panel.classList.add('visible')
      const body = lessonPanel.panel.querySelector('#lesson-body')
      const nav = lessonPanel.panel.querySelector('#lesson-nav')
      lessonBrowser.show(body, nav)
    })

    uiOverlay.onHelp(() => {
      speechOutput.speak(
        "Welcome to EarthVoice. Try saying 'Go to Japan', 'Show population of African countries', or 'What are the largest cities in Europe?'"
      )
    })

    // ── Moon toggle ──────────────────────────────────────────
    uiOverlay.onMoonToggle(() => {
      if (globeSwitcher.isInTransition()) return
      if (globeSwitcher.getCurrentMode() === 'earth') {
        globeSwitcher.switchToMoon()
      } else {
        globeSwitcher.switchToEarth()
      }
    })

    // Disable button during transitions, update icon on completion
    document.addEventListener('globeswitch:start', () => {
      uiOverlay.setMoonToggleEnabled(false)
    })

    document.addEventListener('globeswitch:complete', (e) => {
      const mode = e.detail.mode
      uiOverlay.setMoonToggleEnabled(true)
      uiOverlay.setGlobeMode(mode)

      // Apply/remove moon theme + clear markers
      if (mode === 'moon') {
        document.body.classList.add('moon-mode')
      } else {
        document.body.classList.remove('moon-mode')
        // Clear any Moon markers when returning to Earth
        moonClearMarkers()
      }
    })

    // ── Done! ──────────────────────────────────────────────
    state.isReady = true
    setSplashStatus('Ready')

    // Hide splash with a slight delay for polish
    setTimeout(() => {
      hideSplash()
      uiOverlay.setIdle()
    }, 600)

    console.log('[EarthVoice] Initialized successfully')
  } catch (err) {
    console.error('[EarthVoice] Initialization failed:', err)
    setSplashStatus(`Error: ${err.message}`)
  }
}

// ── Context strip (Phase 2-C-ii) ─────────────────────────────
// A small on-screen pill row showing the active base layer + stacked overlays,
// so the student/teacher can always see what the globe is holding. Region is
// intentionally omitted in v1 (continent scoping doesn't reliably set focus, so
// showing it risks a stale label). Non-critical UI — failures never block a
// command.
let contextStripState = { region: null, base: null, overlays: [] }

function renderContextStrip() {
  let el = document.getElementById('ev-context-strip')
  if (!el) {
    el = document.createElement('div')
    el.id = 'ev-context-strip'
    el.style.cssText =
      'position:fixed;top:96px;left:50%;transform:translateX(-50%);z-index:40;' +
      'display:none;gap:6px;align-items:center;pointer-events:none;' +
      'font:600 12px/1.2 system-ui,-apple-system,sans-serif;'
    document.body.appendChild(el)
  }
  const chips = contextStripChips(contextStripState)
  if (chips.length === 0) {
    el.style.display = 'none'
    return
  }
  el.replaceChildren()
  for (const c of chips) {
    const pill = document.createElement('span')
    const bg = c.kind === 'overlay' ? 'rgba(245,166,35,.18)' : 'rgba(255,255,255,.10)'
    const fg = c.kind === 'overlay' ? '#f5b94d' : '#e6e6e6'
    pill.textContent = (c.kind === 'overlay' ? '+ ' : '') + c.label
    pill.style.cssText =
      `background:${bg};color:${fg};padding:4px 10px;border-radius:999px;` +
      'white-space:nowrap;backdrop-filter:blur(4px);border:1px solid rgba(255,255,255,.08);'
    el.appendChild(pill)
  }
  el.style.display = 'flex'
}

function updateStripAfterRender(intent, text) {
  try {
    const baseLabel = state.pipelineServices?.layers?._activeChoropleth?.displayName || null
    contextStripState = nextContextState(contextStripState, intent, { baseLabel, text })
    renderContextStrip()
  } catch (e) {
    console.debug('[context-strip] update skipped:', e?.message)
  }
}

function clearContextStrip() {
  contextStripState = { region: null, base: null, overlays: [] }
  renderContextStrip()
}

/**
 * Process a command (from voice or text input)
 */
async function processCommand(transcript, uiOverlay, router, opts) {
  opts = opts || {}
  // `compoundStep` marks a clause invoked by the compound executor. Such calls
  // re-enter while the outer command holds the lock, and must NOT clear it.
  const isStep = !!opts.compoundStep
  if (state.isProcessing && !isStep) return

  if (!isStep) state.isProcessing = true
  uiOverlay.setProcessing(transcript)

  // End voice timer if it was started (voice input path)
  if (!isStep && state.lastInputMethod === 'voice') {
    telemetry.endTimer('voice')
  }

  try {
    // Wait for the pipeline operation registry to finish loading. Init
    // kicks it off in parallel, but late-registered ops (joshua_project,
    // overlay_cities, raster) may not be ready when the user fires their
    // first command — pipeline validation would then reject those ops
    // as "unknown".
    if (state.registryReady) await state.registryReady

    // Multi-step compound command (Phase 2-C): split into ordered clauses and
    // run each as its own step — overlays stack on the base choropleth and one
    // combined summary is spoken at the end. Only the outer call decomposes.
    if (!isStep) {
      const turn = classifyTurn(transcript)
      if (turn.kind === 'compound' && turn.parts.length > 1) {
        await runCompound(turn.parts, transcript, uiOverlay, router)
        return
      }
    }

    // Parse intent via Worker API (or direct fallback)
    telemetry.startTimer('ai')
    const intent = await parseIntent(transcript)
    telemetry.endTimer('ai')
    console.log('[EarthVoice] Intent:', intent)

    // Phase 2-C: when this is a non-first compound step whose result is an
    // additive overlay (markers, ellipse, isochrone, …), render it ON TOP of
    // the active base choropleth instead of clearing. The executor's per-step
    // finally calls endOverlay() to reset. Base steps replace as usual.
    if (isStep && opts.stepIndex > 0 && layerRole(intent) === 'overlay') {
      state.pipelineServices?.layers?.beginOverlay()
    }

    // Track analytics
    analytics.trackQuery(intent.type, {
      prompt: transcript,
      inputMethod: state.lastInputMethod || 'typed',
      country: intent.target?.country || intent.country,
      isFastQuery: !!intent._fastQuery,
      isFallback: !!intent._fallback,
      visualization: intent.visualization?.type,
    })

    // Update globe context with intent target info
    const intentCountry = intent.target?.country || intent.country
    if (intentCountry) {
      globeContext.updateFocus(
        intentCountry,
        intent.target?.location || intent.name || intentCountry,
        intent.admin_level || null,
        intent.name || null,
        null
      )
    }

    // Handle "go back" / "return" commands for branch stack
    if (/^(?:go\s+back|return(?:\s+to\s+what\s+I\s+was\s+doing)?|pop)$/i.test(transcript.trim())) {
      if (globeContext.popBranch()) {
        uiOverlay.setResult(transcript, 'Returned to previous analysis.')
        return
      }
    }

    // Reset command — delegate to shared handler that closes over init()'s
    // local layerManager/legend/globe refs. Bare references to those
    // identifiers here throw ReferenceError at module scope.
    if (intent.type === 'reset') {
      if (state.handleReset) await state.handleReset()
      return
    }

    // Question-form queries that the AI tagged as non-educational (e.g. "Why do
    // people live near rivers?" tagged as 'rivers') should be reclassified.
    if (
      ['rivers'].includes(intent.type) &&
      /^(why|how|what|who|when|where|tell)\b/i.test(transcript.trim())
    ) {
      intent.type = 'educational'
    }

    // Educational questions — stream response progressively
    if (intent.type === 'educational') {
      let fullText = ''
      try {
        for await (const chunk of streamEducationalResponse(transcript)) {
          fullText += chunk
          uiOverlay.showStreamingText(fullText)
        }
        uiOverlay.setResult(transcript, fullText)
        // Speak the complete response
        if (router.speechOutput) router.speechOutput.speak(fullText)
      } catch (err) {
        console.warn('[EarthVoice] Stream failed:', err.message)
        uiOverlay.setError('Could not get an answer. Try rephrasing.')
      }
      return
    }

    // ── Cold-start engine gate ──────────────────────────────
    // DuckDB (~33MB) and geolibre (~22MB) load lazily and prewarm in the
    // background. If the user fires the FIRST query that needs one before it's
    // ready, wait here with a visible "warming up" state instead of dispatching
    // against a not-yet-ready engine — which is what made the first analytic /
    // buffer query render blank. Capped so a stuck load can never hang the UI;
    // on timeout we proceed and let the op surface its own message.
    // Since CL-140 DuckDB has NO eager page-load init: waitReady() here is
    // also the on-demand trigger that starts the download when the prewarm
    // scheduler hasn't fired yet.
    try {
      const need = requiredEngines(intent)
      if (need.has('geolibre') && !isGeolibreReady()) {
        uiOverlay.setWarming('Warming up the geometry engine (first query only)…')
        await withTimeout(waitGeolibreReady(), 30000, 'geolibre warm-up')
      }
      if (need.has('duckdb') && !duckdbEngine.isReady) {
        uiOverlay.setWarming('Warming up the analytics engine (first query only)…')
        await withTimeout(duckdbEngine.waitReady(), 30000, 'duckdb warm-up')
      }
      // Restore the normal THINKING status before dispatch.
      if (need.size) uiOverlay.setProcessing(transcript)
    } catch (err) {
      log.warn('engine warm-up wait failed:', err?.message)
      // Proceed anyway — the op's own readiness handling takes over.
    }

    // Route to handler — use Moon router when in Moon mode and intent is lunar
    telemetry.startTimer('data')
    const isMoonMode = globeSwitcher.getCurrentMode() === 'moon'
    const isLunarIntent = intent.type?.startsWith('lunar_')

    if (isMoonMode && isLunarIntent) {
      await state.moonRouter.route(intent)
      telemetry.endTimer('data')
      const rawTemplate = intent.voiceResponse?.template || `"${transcript}"`
      const description = state.moonRouter.interpolateResponse(rawTemplate)
      uiOverlay.setResult(transcript, description)
    } else {
      // Attach original user text so the router can distinguish question-form
      // queries ("How many schools in Germany?") from action commands.
      intent._originalTranscript = transcript

      // ── Pipeline routing (Phase 2→4) ────────────────────────
      // 0. Native pipeline arrays from Claude (Phase 4) → execute directly
      // 1. Check for forced POI intercept (misrouted POI queries)
      // 2. Check for SQL-bearing intents → DuckDB query op
      // 3. Convert legacy intent to pipeline via adapter
      // 4. Fall through to legacy router for unhandled types

      // Clear stale raster layers when a non-raster command fires
      const RASTER_TYPES = new Set(['render_raster', 'remove_raster', 'adjust_opacity'])
      const isRasterCommand =
        RASTER_TYPES.has(intent.type) ||
        (Array.isArray(intent.pipeline) && intent.pipeline.some((s) => RASTER_TYPES.has(s.op)))
      if (!isRasterCommand) {
        try {
          const { clearAllRasterLayers } = await import('./ai/ops/render-raster.js')
          const map = state.pipelineServices.animator?.globe?.map
          if (map) clearAllRasterLayers(map)
        } catch {}
      }

      const ctx = createContext(state.pipelineServices)
      let routed = false
      const resultSeqBefore = state.resultSeq || 0

      // Spatial Analysis Advisor — runs once per command on ALL dispatch
      // paths (Native pipeline, Forced POI, SQL/DuckDB, adapter, legacy).
      // Stashes the enrichment on the router so the overlay result panel
      // can render the Spatial Insights card, and sets a classification
      // engine hint for this op. Wrapped in try/catch + finally so a
      // failure here never blocks the command.
      try {
        const { adviseSpatialMethod } = await import('./ai/spatial-advisor.js')
        const { classificationEngine } = await import('./layers/classification-engine.js')
        const advice = adviseSpatialMethod(intent)
        router._lastSpatialAdvice = advice
        classificationEngine.setAdvisorHint({
          suggestedClassification: advice.ev_classification,
          color_ramp_type: advice.color_ramp_type,
          method: advice.method,
        })
      } catch (err) {
        console.debug('[spatial-advisor] pre-dispatch advisor failed:', err)
        router._lastSpatialAdvice = null
      }

      // Step 0: Native pipeline from Claude (Phase 4)
      if (Array.isArray(intent.pipeline)) {
        console.log(
          `[main] Native pipeline (${intent.pipeline.length} steps):`,
          intent.pipeline.map((s) => s.op).join(' → ')
        )
        try {
          await executePipeline(intent.pipeline, ctx)
          routed = true
        } catch (err) {
          console.warn('[main] Native pipeline execution failed:', err.message)
          // Fall through to adapter/legacy as fallback
        }
      }

      // Step 1: Forced POI detection — catches misrouted "Show me hospitals in USA"
      if (!routed)
        try {
          const { detectForcedPOIQuery } = await import('./ai/ops/forced-poi.js')
          const forcedPOI = detectForcedPOIQuery(intent, ctx.services)
          if (forcedPOI) {
            // CL-140: both forced_poi and poi_in_admin run on DuckDB, but this
            // intercept fires AFTER the cold-start gate inspected the ORIGINAL
            // intent (which carries no duckdb marker). With lazy init the
            // engine may not even have started downloading yet — warm it here
            // with the same gate UX before dispatching.
            if (!duckdbEngine.isReady) {
              uiOverlay.setWarming('Warming up the analytics engine (first query only)…')
              try {
                await withTimeout(duckdbEngine.waitReady(), 30000, 'duckdb warm-up')
              } catch (err) {
                log.warn('duckdb warm-up for forced POI failed:', err?.message)
              }
              uiOverlay.setProcessing(transcript)
            }
            // Sub-national unit ("hospitals in Maharashtra") → count POIs inside
            // that one polygon via poi_in_admin. Country-level ("hospitals in
            // India") stays on the country-wide forced_poi path. A location that
            // is NOT itself a country, and differs from the resolved country
            // code, is sub-national.
            const abs = ctx.services.adminBoundaries
            const loc = forcedPOI.location
            const locIsCountry = !!(loc && abs?.isReady && abs.getCountry(loc))
            const isSubNational =
              !!loc &&
              !locIsCountry &&
              String(loc).toUpperCase() !== String(forcedPOI.country || '').toUpperCase()

            if (isSubNational) {
              console.log(
                `[main] POI-in-admin redirect: category=${forcedPOI.category}, country=${forcedPOI.country}, unit=${loc}`
              )
              await executePipeline(
                [
                  {
                    op: 'poi_in_admin',
                    category: forcedPOI.category,
                    country: forcedPOI.country,
                    name: loc,
                    originalIntent: intent,
                  },
                ],
                ctx
              )
            } else {
              console.log(
                `[main] Forced POI redirect: category=${forcedPOI.category}, country=${forcedPOI.country}`
              )
              await executePipeline(
                [
                  {
                    op: 'forced_poi',
                    category: forcedPOI.category,
                    country: forcedPOI.country,
                    location: forcedPOI.location,
                    originalIntent: intent,
                  },
                ],
                ctx
              )
            }
            routed = true
          }
        } catch (err) {
          console.warn('[main] Forced POI detection failed:', err.message)
        }

      // Step 1.5: H3 density safety-net — catch AI-generated intents that
      // request H3 visualization but weren't handled by fast-query regex.
      // Reclassify as h3_density so the adapter routes to the H3 pipeline op.
      if (!routed && intent.visualization?.type === 'h3-density' && intent.type !== 'h3_density') {
        console.log('[main] H3 density safety-net: reclassifying intent to h3_density')
        intent.type = 'h3_density'
        if (!intent.poi_category && typeof intent.operation?.filter?.value === 'string') {
          intent.poi_category = intent.operation.filter.value
        }
      }

      // Step 2: SQL-bearing intents → DuckDB query op
      if (!routed && intent.sql) {
        // Clear stale legend
        router.layers?.legendCallback?.({ visible: false })

        // Dual-path: activate PMTiles for POI queries
        const isPOIQuery =
          intent.target?.dataset === 'places' &&
          intent.visualization?.type === 'poi-markers' &&
          intent.sql
        if (isPOIQuery) {
          const globe = state.pipelineServices.animator?.globe
          if (globe?.overture) {
            const category = intent.operation?.filter?.value || intent.target?.category || null
            globe.overture.toggleTheme('places', true, { category })
          }
        }

        // Try DuckDB query via pipeline
        try {
          const { duckdbEngine } = await import('./data/duckdb-engine.js')
          if (duckdbEngine.isReady) {
            await executePipeline(
              [
                {
                  op: 'duckdb_query',
                  sql: intent.sql,
                  intent,
                  pmtilesActive: isPOIQuery,
                },
              ],
              ctx
            )
            routed = true
          } else if (isPOIQuery) {
            // PMTiles is already active — that's enough
            routed = true
          }
        } catch (err) {
          console.warn('[main] DuckDB pipeline query failed:', err.message)
          // Fall through to adapter/legacy
        }

        // If DuckDB failed and the intent targets an aggregation-only table, show error
        if (!routed) {
          const aggOnlyTables = [
            'country_summary',
            'continent_summary',
            'admin_summary',
            'city_rankings',
            'temporal_population',
            'country_indicators',
            'country_indicators_latest',
          ]
          if (intent.target?.dataset && aggOnlyTables.includes(intent.target.dataset)) {
            console.warn(
              `[main] Aggregation table "${intent.target.dataset}" requires DuckDB — no local fallback`
            )
            await state.pipelineServices.speech?.speak(
              'That query needs the analytics engine. Please wait a moment and try again.'
            )
            routed = true
          }
        }
      }

      // Step 3: Pipeline adapter for non-SQL intents
      if (!routed) {
        const adapted = adaptIntent(intent)
        if (adapted.handled) {
          await executePipeline(adapted.pipeline, ctx)
          routed = true
        }
      }

      // Step 4: Legacy fallback
      if (!routed) {
        await router.route(intent)
      }

      // CL-261: a new command that produced no table of its own must not leave
      // the previous command's table on screen ("Go to France" kept showing
      // Japan's prefectures). Restyle / camera-only tweaks keep it, as does
      // stack mode, where earlier layers stay on the globe.
      if (
        state.resultSeq === resultSeqBefore &&
        !isResultPreservingIntent(intent) &&
        !state.pipelineServices.layers?.isStackMode?.()
      ) {
        uiOverlay.hideResultPanel()
      }

      // Clear the per-op advisor hint on the classification engine so it
      // doesn't leak into the next command.
      try {
        const { classificationEngine } = await import('./layers/classification-engine.js')
        classificationEngine.setAdvisorHint(null)
      } catch {}

      telemetry.endTimer('data')

      // Sync pipeline results back to legacy state for UI compatibility
      if (ctx.resultCount != null) router._lastResultCount = ctx.resultCount
      if (ctx.resultRow != null) router._lastResultRow = ctx.resultRow
      if (ctx.lastSQL != null) router._lastSQL = ctx.lastSQL
      if (ctx.spokeDuringRoute) router._spokeDuringRoute = true

      // Post-route voice response: if the pipeline didn't speak, speak the template
      if (
        routed &&
        !ctx.spokeDuringRoute &&
        intent.voiceResponse?.shouldSpeak &&
        intent.voiceResponse?.template
      ) {
        const { interpolateTemplate } = await import('./ai/pipeline-utils.js')
        const spokenText = interpolateTemplate(intent.voiceResponse.template, ctx)
        await state.pipelineServices.speech?.speak(spokenText)
        router._spokeDuringRoute = true
      }

      // Update globe context with result info
      globeContext.setLastResult({
        type: intent.type || (intent.pipeline ? 'pipeline' : 'unknown'),
        featureCount: router._lastResultCount || null,
        dataset: intent.target?.dataset || null,
      })

      const rawTemplate = intent.voiceResponse?.template || `"${transcript}"`
      const description = router.interpolateResponse(rawTemplate)
      uiOverlay.setResult(transcript, description)

      // Phase 2-C-ii: refresh the on-screen context strip (base + overlays).
      // For compound commands each step updates it, so the final strip shows the
      // full stack (e.g. "GDP per Capita · + hospitals"). The clause text gives
      // overlay pills a friendly label from the student's own words.
      updateStripAfterRender(intent, transcript)
    }

    // Send telemetry (fire-and-forget)
    telemetry.endTimer('render')
    telemetry.sendTelemetry({
      transcript,
      inputMethod: state.lastInputMethod || 'typed',
      intentType: intent.type,
      intentTarget: intent.target?.location || intent.country || intent.name || null,
      contextAction: intent.context_action || null,
      resultStatus: 'success',
      resultSummary: intent.voiceResponse?.template || null,
      featureCount: router._lastResultCount || null,
      datasetUsed: intent.target?.dataset || null,
    })
  } catch (err) {
    console.error('[EarthVoice] Command failed:', err)
    analytics.trackError(transcript, err.message)
    // Compound steps rethrow so the executor can record the failure and keep
    // going; they do not paint per-step error UI (the summary covers it).
    if (isStep) throw err
    uiOverlay.setError(`Something went wrong: ${err.message}`)

    // Send error telemetry
    telemetry.sendTelemetry({
      transcript,
      inputMethod: state.lastInputMethod || 'typed',
      resultStatus: 'error',
      resultSummary: err.message,
    })
  } finally {
    // Only the outer command owns the lock + idle reset; steps must not touch it.
    if (!isStep) {
      state.isProcessing = false

      // Reset to idle after a delay
      setTimeout(() => {
        if (!state.isProcessing) {
          uiOverlay.setIdle()
        }
      }, 5000)
    }
  }
}

/**
 * Compound executor (Phase 2-C). Runs each clause of a compound command in
 * order via processCommand(..., { compoundStep:true, stepIndex }). Overlay steps
 * stack on the base choropleth; per-step speech is captured and replaced by ONE
 * combined summary spoken at the end. A clause that throws is skipped and the
 * sequence continues (partial-failure tolerance).
 */
async function runCompound(parts, originalTranscript, uiOverlay, router) {
  const services = state.pipelineServices || {}
  const layers = services.layers || null
  const speech = services.speech || null

  // Mute per-step speech, capturing it for one end-of-sequence summary.
  const captured = []
  const realSpeak = speech && typeof speech.speak === 'function' ? speech.speak.bind(speech) : null
  if (speech && realSpeak) {
    speech.speak = (t) => {
      if (t) captured.push(String(t))
      return Promise.resolve()
    }
  }
  // Educational / streamed paths speak via router.speechOutput — mute that too.
  const routerSpeaker = router && router.speechOutput
  const realRouterSpeak =
    routerSpeaker && typeof routerSpeaker.speak === 'function'
      ? routerSpeaker.speak.bind(routerSpeaker)
      : null
  if (realRouterSpeak) {
    routerSpeaker.speak = (t) => {
      if (t) captured.push(String(t))
      return Promise.resolve()
    }
  }

  let okCount = 0
  try {
    for (let i = 0; i < parts.length; i++) {
      const clause = parts[i]
      try {
        await processCommand(clause, uiOverlay, router, { compoundStep: true, stepIndex: i })
        okCount++
      } catch (err) {
        console.warn(`[compound] step ${i + 1} failed: "${clause}" — ${err.message}`)
      } finally {
        if (layers) layers.endOverlay()
      }
    }
  } finally {
    // Always restore real speech + leave overlay mode off.
    if (speech && realSpeak) speech.speak = realSpeak
    if (realRouterSpeak) routerSpeaker.speak = realRouterSpeak
    if (layers) layers.endOverlay()
  }

  // One combined summary at the end.
  let summary
  if (captured.length) {
    summary = captured.join(' ')
  } else if (okCount > 0) {
    summary = `Done — ${okCount} step${okCount > 1 ? 's' : ''} of "${originalTranscript}".`
  } else {
    summary = `I couldn't complete "${originalTranscript}".`
  }
  if (realSpeak && summary) {
    try {
      await realSpeak(summary)
    } catch (e) {
      console.debug('[compound] summary speak failed:', e?.message)
    }
  }
  uiOverlay.setResult(originalTranscript, summary)
}

/**
 * Load states/provinces + 50m cities + 50m clipped countries in background
 * and rebuild the location index.
 */
async function loadDetailedDataInBackground(animator, router, layerManager) {
  try {
    const [statesData, cities50] = await Promise.all([
      loadDataset('states_provinces', 'medium'),
      loadDataset('cities', 'medium'),
    ])

    // Merge 110m + 50m city features for the richest possible city index
    const mergedCities = {
      type: 'FeatureCollection',
      features: [...(state.citiesData?.features || []), ...(cities50?.features || [])],
    }

    // Rebuild location index with all data
    state.locationIndex = buildLocationIndex(state.countriesData, statesData, mergedCities)

    animator.setLocationIndex(state.locationIndex)
    router.update({ locationIndex: state.locationIndex })
    if (state.pipelineServices) {
      state.pipelineServices.locationIndex = state.locationIndex
    }

    console.log(
      `[EarthVoice] Detailed data loaded — index has cities from both 110m and 50m datasets`
    )
  } catch (err) {
    console.warn('[EarthVoice] Failed to load detailed data:', err.message)
  }
}

// ── Service Worker registration ─────────────────────────────
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch((err) => {
    console.warn('[EarthVoice] Service Worker registration failed:', err.message)
  })
}

// ── Start ──────────────────────────────────────────────────
init()
